/**
 * Backend Image Service - Uses the new PostgreSQL backend API
 * This service is used when REACT_APP_USE_BACKEND_API=true
 *
 * Caching Layer: staticDataService (CDN) -> in-memory -> localStorage -> live API
 */

import { Image } from '../types';
import { apiService } from './apiService';
import { staticDataService } from './staticDataService';

class BackendImageService {
    private cache: Image[] = [];
    private cacheTimestamp: number = 0;
    private readonly CACHE_DURATION = 4 * 60 * 60 * 1000;
    private readonly STORAGE_KEY = 'images_cache_backend';
    private pendingRequest: Promise<Image[]> | null = null;

    private loadCacheFromStorage(): void {
        try {
            const cachedData = localStorage.getItem(this.STORAGE_KEY);
            const cachedTimestamp = localStorage.getItem(`${this.STORAGE_KEY}_timestamp`);

            if (cachedData && cachedTimestamp) {
                const timestamp = parseInt(cachedTimestamp, 10);
                if (Date.now() - timestamp < this.CACHE_DURATION) {
                    this.cache = JSON.parse(cachedData);
                    this.cacheTimestamp = timestamp;
                }
            }
        } catch (error) {
            console.error('Error loading images cache:', error);
        }
    }

    private saveCacheToStorage(images: Image[]): void {
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(images));
            localStorage.setItem(`${this.STORAGE_KEY}_timestamp`, Date.now().toString());
        } catch (error) {
            console.error('Error saving images cache:', error);
        }
    }

    constructor() {
        this.loadCacheFromStorage();
    }

    async getAllImages(activeOnly: boolean = false, forceRefresh: boolean = false): Promise<Image[]> {
        // 1. Static CDN Cache – instant, zero backend dependency
        if (!forceRefresh) {
            const staticImgs = staticDataService.getAllImages();
            if (staticImgs.length > 0) {
                if (this.cache.length === 0) {
                    this.cache = staticImgs.map(this.mapBackendImage);
                    this.cacheTimestamp = Date.now();
                    this.saveCacheToStorage(this.cache);
                }
                // SWR: silently revalidate in the background
                this.fetchFromBackend(activeOnly).catch(() => { });
                const filtered = activeOnly ? this.cache.filter(img => img.active !== false) : this.cache;
                return [...filtered];
            }
        }

        // 2. If we have cached data, return it immediately to provide "instant" load
        if (!forceRefresh && this.cache.length > 0) {
            const filtered = activeOnly ? this.cache.filter(img => img.active !== false) : this.cache;

            // If cache is older than 5 minutes, refresh in background
            if (Date.now() - this.cacheTimestamp > 5 * 60 * 1000) {
                this.fetchFromBackend(activeOnly).catch(() => { });
            }
            return [...filtered];
        }

        // Force refresh or no cache - fetch from backend
        if (forceRefresh) {
            this.clearCache();
        }

        if (this.pendingRequest) {
            const result = await this.pendingRequest;
            return activeOnly ? result.filter(img => img.active !== false) : result;
        }

        this.pendingRequest = this.fetchFromBackend(activeOnly);
        try {
            const result = await this.pendingRequest;
            this.pendingRequest = null;
            return result;
        } catch (error) {
            this.pendingRequest = null;
            if (this.cache.length === 0) {
                this.loadCacheFromStorage();
            }
            return activeOnly ? this.cache.filter(img => img.active !== false) : [...this.cache];
        }
    }

    clearCache(): void {
        this.cache = [];
        this.cacheTimestamp = 0;
        localStorage.removeItem(this.STORAGE_KEY);
        localStorage.removeItem(`${this.STORAGE_KEY}_timestamp`);
    }

    private async fetchFromBackend(activeOnly: boolean): Promise<Image[]> {
        try {
            const images = await apiService.getAllImages(activeOnly);
            const normalizedImages = images.map(this.mapBackendImage);
            this.cache = normalizedImages;
            this.cacheTimestamp = Date.now();
            this.saveCacheToStorage(normalizedImages);

            // Hydrate the static cache layer with fresh live data (SWR)
            staticDataService.hydrateLiveImages(normalizedImages);

            return normalizedImages;
        } catch (error) {
            console.error('Error fetching images from backend:', error);
            // Last resort: return the static snapshot if available
            const staticImgs = staticDataService.getAllImages();
            if (staticImgs.length > 0) return staticImgs.map(this.mapBackendImage);
            throw error;
        }
    }

    async getImagesByCategory(category: string): Promise<Image[]> {
        // Check if we have the main images cache - filter from it for instant results
        if (this.cache && this.cache.length > 0) {
            const filtered = this.cache.filter(img => img.category === category);
            if (filtered.length > 0) {
                // Background refresh but return cache immediately
                apiService.getImagesByCategory(category)
                    .then(images => {
                        const normalized = images.map(this.mapBackendImage);
                        // Update main cache with fresh data
                        normalized.forEach(ni => {
                            const idx = this.cache.findIndex(c => c.id === ni.id);
                            if (idx !== -1) this.cache[idx] = ni;
                            else this.cache.push(ni);
                        });
                        this.saveCacheToStorage(this.cache);
                    })
                    .catch(() => { });
                return filtered;
            }
        }

        try {
            const images = await apiService.getImagesByCategory(category);
            const normalized = images.map(this.mapBackendImage);
            // Optional: update main cache too
            normalized.forEach(ni => {
                const idx = this.cache.findIndex(c => c.id === ni.id);
                if (idx !== -1) this.cache[idx] = ni;
                else this.cache.push(ni);
            });
            this.saveCacheToStorage(this.cache);
            return normalized;
        } catch (error) {
            console.error('Error fetching images by category:', error);
            return [];
        }
    }

    private mapBackendImage(img: any): Image {
        return {
            id: img.id,
            category: img.category,
            name: img.name,
            description: img.description || '',
            url: img.url,
            altText: img.alt_text || img.altText || '',
            fileSize: img.file_size || img.fileSize || 0,
            mimeType: img.mime_type || img.mimeType || '',
            width: img.width || 0,
            height: img.height || 0,
            active: img.active !== false,
            featured: img.featured === true,
            sortOrder: img.sort_order || img.sortOrder || 0,
            createdAt: img.created_at || img.createdAt || new Date().toISOString(),
            updatedAt: img.updated_at || img.updatedAt || new Date().toISOString()
        };
    }

    async uploadImage(file: File): Promise<{ url: string }> {
        try {
            const result = await apiService.uploadToCloudinary(file);
            return { url: result.url };
        } catch (error) {
            console.error('Error uploading image to Cloudinary:', error);
            throw error;
        }
    }

    async uploadAndCreateImage(file: File, category: string): Promise<Image> {
        try {
            // 1. Upload to Cloudinary via unified endpoint
            const uploadResult = await this.uploadImage(file);
            
            // 2. Create database record
            const imageData = {
                category,
                name: file.name.split('.')[0],
                url: uploadResult.url,
                file_size: file.size,
                mime_type: file.type,
                active: true,
                featured: false,
                sort_order: 0
            };

            return await this.addImage(imageData);
        } catch (error) {
            console.error('Error in uploadAndCreateImage:', error);
            throw error;
        }
    }

    async addImage(imageData: any): Promise<Image> {
        const newImg = await apiService.createImage(imageData);
        this.clearCache();
        return this.mapBackendImage(newImg);
    }

    async updateImage(id: string, updates: any): Promise<Image> {
        const updatedImg = await apiService.updateImage(id, updates);
        this.clearCache();
        return this.mapBackendImage(updatedImg);
    }

    async deleteImage(id: string): Promise<boolean> {
        const success = await apiService.deleteImage(id);
        this.clearCache();
        return success;
    }

    async toggleImageActive(id: string): Promise<boolean> {
        const images = await this.getAllImages();
        const img = images.find(i => i.id === id);
        if (!img) return false;
        await this.updateImage(id, { active: !img.active });
        return true;
    }

    async toggleImageFeatured(id: string): Promise<boolean> {
        const images = await this.getAllImages();
        const img = images.find(i => i.id === id);
        if (!img) return false;
        await this.updateImage(id, { featured: !img.featured });
        return true;
    }
}

export const backendImageService = new BackendImageService();
export default backendImageService;
